// Touch cue controls: the desktop model (mouse look, hold Space to lock the aim, stroke the mouse back and forward)
// on a touch screen. Nothing new is invented: the same three actions, driven by fingers instead of the mouse and
// the Space key, and the stroke goes through the same StrokeTracker (stroke.ts), so the finger's speed becomes the
// ball's speed exactly as the mouse's does (same window, gain, sensitivity setting and caps).
//
//   look / aim   one finger dragged on the table        = moving the mouse (same formula: angle += px * aim / 200,
//                                                          with finger px × TOUCH_GAIN, see below)
//   lock aim     hold the on-screen LOCK button          = holding Space
//                (or: keep one finger on the table and put a second finger down; the first finger is then the
//                "Space" finger for as long as it stays down, and the second one strokes)
//   stroke       with the aim locked, drag a finger down (draws the cue back, like pulling the mouse towards you)
//                and push it up through the ball         = the mouse stroke (finger px × TOUCH_GAIN = mouse px)
//   release      lift the lock finger                    = releasing Space (the cue keeps its draw-back, as on desktop)
//
// A finger that is already looking when the lock starts becomes the stroke finger (like moving the mouse and then
// pressing Space), and a stroke finger still down when the lock ends goes back to looking (like releasing Space
// mid-movement). This module is pure (no DOM): touchInput.ts feeds it pointer events, touchControls.test.ts checks it.
//
// TOUCH_GAIN: a finger covers far fewer CSS px than a mouse reports in movementX/Y under pointer lock for the same
// intended move, so with finger px = mouse px touch aim and stroke felt about half as strong as the mouse. Every
// finger delta (look dx/dy and stroke dy) is multiplied by TOUCH_GAIN here, before it reaches the shared formulas
// (lookAngles' px * aim / 200 and the StrokeTracker). It is touch-only: the mouse path (Events.ts) never comes
// through here, and the Settings sliders (aimSensitivity / strokeSensitivity) still apply on top, unchanged.
// devicePixelRatio is deliberately not involved: CSS px are already density-independent.
// Side effect (intended): the stroke's arm (MIN_BACK_PX 30) and fire (FIRE_PX 38) thresholds take half the finger travel.

/** Finger CSS px -> mouse-equivalent px, for look and stroke on the touch path only. */
export const TOUCH_GAIN = 2;

export interface TouchCueActions {
    /** mouse-look equivalent: pixel deltas (already × TOUCH_GAIN) (dx > 0 finger moved right, dy > 0 finger moved down) */
    look(dx: number, dy: number): void;
    /** Space pressed */
    startLock(t: number): void;
    /** Space released */
    endLock(): void;
    /** mouse moved with Space held (dy already × TOUCH_GAIN): dy > 0 draws the cue back, dy < 0 pushes it through the ball */
    stroke(dy: number, t: number): void;
}

export type TouchRole = "look" | "lock" | "stroke";

interface Finger { x: number; y: number; role: TouchRole }

export class TouchCueController {
    private fingers = new Map<number, Finger>();
    /** pointers holding the lock: the on-screen button's, or the first finger in the second-finger hold */
    private lockers = new Set<number>();

    /** gain: finger px -> mouse-equivalent px (TOUCH_GAIN; 1 = raw finger px) */
    constructor(private actions: TouchCueActions, private gain: number = TOUCH_GAIN) {}

    get locked() { return this.lockers.size > 0; }

    role(id: number): TouchRole | null {
        if (this.lockers.has(id) && !this.fingers.has(id)) return "lock";
        const f = this.fingers.get(id);
        return f ? f.role : null;
    }

    private strokeFinger(): number | null {
        let found: number | null = null;
        this.fingers.forEach((f, id) => { if (f.role === "stroke" && found === null) found = id; });
        return found;
    }

    private beginLock(id: number, t: number) {
        const was = this.locked;
        this.lockers.add(id);
        if (was) return;
        this.actions.startLock(t);
        // a finger that was looking carries on as the stroke finger
        let promoted = false;
        this.fingers.forEach((f, fid) => {
            if (!promoted && f.role === "look" && !this.lockers.has(fid)) { f.role = "stroke"; promoted = true; }
        });
    }

    private releaseLock(id: number) {
        if (!this.lockers.delete(id) || this.locked) return;
        this.actions.endLock();
        this.fingers.forEach(f => { if (f.role === "stroke") f.role = "look"; });
    }

    /** The on-screen lock button went down / up (pointer id of the finger on it). */
    lockDown(id: number, t: number) { this.beginLock(id, t); }
    lockUp(id: number) { this.releaseLock(id); }

    /** A finger touched the table. */
    down(id: number, x: number, y: number, t: number) {
        if (this.fingers.has(id)) this.up(id);
        let role: TouchRole = "look";
        if (this.locked) {
            role = this.strokeFinger() === null ? "stroke" : "look";
        } else {
            // second-finger hold: a finger already on the table becomes the lock ("Space") finger, the new one strokes
            let holder: number | null = null;
            this.fingers.forEach((f, fid) => { if (holder === null && f.role === "look") holder = fid; });
            if (holder !== null) {
                const h = this.fingers.get(holder)!;
                h.role = "lock";
                this.fingers.set(id, {x, y, role: "stroke"});
                this.lockers.add(holder);
                this.actions.startLock(t);
                return;
            }
        }
        this.fingers.set(id, {x, y, role});
    }

    /** A finger on the table moved to (x, y) at t (event timestamp, ms). */
    move(id: number, x: number, y: number, t: number) {
        const f = this.fingers.get(id);
        if (!f) return;
        const dx = x - f.x, dy = y - f.y;
        f.x = x; f.y = y;
        if (dx === 0 && dy === 0) return;
        const g = this.gain;
        if (f.role === "look" && !this.locked) this.actions.look(dx * g, dy * g);
        else if (f.role === "stroke" && this.locked) this.actions.stroke(dy * g, t);
    }

    /** A finger left the table (or the browser cancelled it). */
    up(id: number) {
        const f = this.fingers.get(id);
        if (!f) return;
        this.fingers.delete(id);
        if (f.role === "lock") this.releaseLock(id);
    }

    /** Everything up (page hidden, mode switched, dialog opened). */
    cancelAll() {
        this.fingers.clear();
        if (this.lockers.size) { this.lockers.clear(); this.actions.endLock(); }
    }
}

/** The mouse-look formula (Events.onMouseMove), for a finger's pixel deltas (already × TOUCH_GAIN by the controller). */
export function lookAngles(h: number, v: number, dx: number, dy: number, aim: number): {h: number, v: number} {
    const nh = h - dx * aim / 200;
    const nv = v + dy * aim / 200;
    return {h: nh, v: nv < Math.PI / 2 && nv > Math.PI / 16 ? nv : v};
}

/** The mouse wheel's camera distance limits (Events.onMouseWheel), for the touch zoom buttons. */
export const ZOOM = {MIN: 0, MAX: 15.5, STEP: 1};

/** Camera distance after `steps` zoom steps (+ = further away); like the wheel, a step past a limit is ignored. */
export function zoomDistance(d: number, steps: number): number {
    const next = d + steps * ZOOM.STEP;
    return next > ZOOM.MIN && next < ZOOM.MAX ? next : d;
}
